import React from 'react';
import { BarChart3, TrendingUp, DollarSign, Calendar, TrendingDown, Clock } from 'lucide-react';
import { useMarketData } from '../hooks/useMarketData';
import MarketIndicator from '../components/MarketIndicator';
import { useSEO } from '../hooks/useSEO';

const Market: React.FC = () => {
  useSEO({
    title: 'Indicadores do Mercado Financeiro Hoje: Selic, CDI, IPCA, Dólar e Ibovespa | O Teu Dinheiro',
    description: 'Acompanhe em tempo real os principais indicadores econômicos do Brasil em 2026: Taxa Selic, CDI, IPCA, cotação do Dólar, Euro, Bitcoin e Ibovespa.',
    keywords: 'indicadores econômicos hoje, taxa selic hoje, CDI hoje, IPCA acumulado, cotação dólar hoje, mercado financeiro 2026',
    url: 'https://www.oteudinheiro.com.br/mercado'
  });
  const { data: marketData, loading, error, lastUpdate, refresh } = useMarketData();

  const formatValue = (value: number | undefined, type: 'percentage' | 'currency' | 'points') => {
    // Ensure value is a number
    const numValue = typeof value === 'number' ? value : 0;

    switch (type) {
      case 'percentage':
        return `${numValue.toFixed(2)}%`;
      case 'currency':
        return `R$ ${numValue.toFixed(2)}`;
      case 'points':
        return `${numValue.toLocaleString('pt-BR')} pontos`;
      default:
        return numValue.toString();
    }
  };

  console.log(marketData);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-600 to-slate-700 text-white py-20">
        <div className="container-custom text-center">
          <BarChart3 className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Análise de Mercado
          </h1>
          <p className="text-xl md:text-2xl text-slate-100 max-w-3xl mx-auto">
            Acompanhe as tendências e análises do mercado financeiro brasileiro
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Análises e tendências do mercado
          </h2>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                O que acompanhar no mercado
              </h3>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-slate-100 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Taxa Selic</h4>
                    <p className="text-gray-600 text-sm">
                      Taxa básica de juros que influencia todos os investimentos de renda fixa
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-slate-100 p-2 rounded-lg">
                    <DollarSign className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Inflação (IPCA)</h4>
                    <p className="text-gray-600 text-sm">
                      Índice oficial de inflação que afeta o poder de compra e investimentos
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-slate-100 p-2 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Ibovespa</h4>
                    <p className="text-gray-600 text-sm">
                      Principal índice da bolsa brasileira, reflete o desempenho das ações
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-slate-100 p-2 rounded-lg">
                    <Calendar className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Câmbio (Dólar)</h4>
                    <p className="text-gray-600 text-sm">
                      Valor do real em relação ao dólar, importante para importações e exportações
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Indicadores atuais
                  </h3>
                  {lastUpdate && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Atualizado: {lastUpdate}</span>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">
                      Erro ao carregar dados: {error}
                    </p>
                    <button
                      onClick={refresh}
                      className="mt-2 text-red-600 hover:text-red-700 text-sm underline"
                    >
                      Tentar novamente
                    </button>
                  </div>
                )}

                <div className="space-y-4">
                  <MarketIndicator
                    label="Taxa Selic"
                    value={marketData ? formatValue(marketData.selic.value, 'percentage') : '15%'}
                    change={marketData ? marketData.selic.change : 0}
                    isLoading={loading}
                    bgColor="bg-green-50"
                    textColor="text-green-600"
                    icon={<TrendingUp className="w-5 h-5" />}
                  />

                  <MarketIndicator
                    label="IPCA (12 meses)"
                    value={marketData ? formatValue(marketData.ipca.value, 'percentage') : '4,62%'}
                    change={marketData ? marketData.ipca.change : 0}
                    isLoading={loading}
                    bgColor="bg-blue-50"
                    textColor="text-blue-600"
                    icon={<BarChart3 className="w-5 h-5" />}
                  />

                  <MarketIndicator
                    label="Ibovespa"
                    value={marketData ? formatValue(marketData.ibovespa.value, 'points') : '127.500 pontos'}
                    change={marketData ? marketData.ibovespa.change : 0}
                    isLoading={loading}
                    bgColor="bg-purple-50"
                    textColor="text-purple-600"
                    icon={<BarChart3 className="w-5 h-5" />}
                  />

                  <MarketIndicator
                    label="Dólar"
                    value={marketData ? formatValue(marketData.usdBrl.value, 'currency') : 'R$ 4,95'}
                    change={marketData ? marketData.usdBrl.change : 0}
                    isLoading={loading}
                    bgColor="bg-orange-50"
                    textColor="text-orange-600"
                    icon={<DollarSign className="w-5 h-5" />}
                  />
                </div>

                <p className="text-xs text-gray-500 mt-4 text-center">
                  * Dados em tempo real, atualizados a cada minuto
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Analysis Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Análises semanais
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                Mercado em alta
              </h3>
              <p className="text-gray-600 mb-4">
                O mercado brasileiro tem mostrado resiliência diante dos desafios globais.
                A inflação controlada e a manutenção da taxa Selic têm criado um ambiente
                favorável para investimentos.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Inflação dentro da meta</li>
                <li>• Taxa de juros estável</li>
                <li>• Crescimento econômico positivo</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingDown className="w-5 h-5 text-red-600 mr-2" />
                Riscos a monitorar
              </h3>
              <p className="text-gray-600 mb-4">
                Apesar do cenário positivo, é importante ficar atento a alguns fatores
                que podem impactar o mercado brasileiro nos próximos meses.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Volatilidade global</li>
                <li>• Eleições nos EUA</li>
                <li>• Tensionamentos geopolíticos</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Tips Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Dicas de investimento para o momento
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Renda Fixa
              </h3>
              <p className="text-gray-600">
                Com a Selic em 15%, os investimentos de renda fixa continuam
                atrativos para quem busca segurança e rendimento previsível.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Renda Variável
              </h3>
              <p className="text-gray-600">
                A bolsa brasileira pode oferecer oportunidades para investidores
                de longo prazo, especialmente em setores defensivos.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Diversificação
              </h3>
              <p className="text-gray-600">
                Mantenha uma carteira diversificada para reduzir riscos
                e aproveitar diferentes oportunidades do mercado.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Mantenha-se informado sobre o mercado!
          </h2>
          <p className="text-xl text-slate-100 mb-8 max-w-2xl mx-auto">
            O conhecimento do mercado é fundamental para tomar decisões
            de investimento mais inteligentes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/investimentos"
              className="btn bg-white text-slate-600 hover:bg-gray-100"
            >
              Ver Investimentos
            </a>
            <a
              href="/blog"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-slate-600"
            >
              Ler Análises
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Market;
